// The console side of the server's two "come back later" answers
// (server/ratelimit.go). Both reach the browser as a status plus Retry-After,
// and both are shown by pages that otherwise only ever report that a password
// was wrong — which is exactly the confusion this module exists to prevent.
//
// A THROTTLE (429) means too many failures from this source lately, so the
// server refused to look at the credential this time. A CAPACITY refusal (503)
// means every argon2 slot was held, so no hash ran at all. In neither case was
// the password judged, and in neither case does trying harder help: the one
// useful thing the operator can be told is how long to wait. "Sign-in failed"
// sends a watchkeeper hunting for a password that was never wrong; "try again
// in 34 s" is a fact they can act on, and at 03:00 that difference is the whole
// value of the message.
//
// The wording lives here rather than at the two call sites so that both pages
// say the same thing, and so the sentences can be tested without a DOM.

/** A wait, written out. Whole seconds under a minute (the common case: the
 *  per-account rule is a flat 15 s and the ops gate starts at 5 s), minutes and
 *  seconds above it, since per-IP backoff escalates to a 5-minute ceiling and
 *  "287 s" is not a number anyone reads as "under five minutes". */
export function fmtWait(seconds: number): string {
  const s = Math.max(1, Math.ceil(seconds));
  if (s < 60) return `${s} s`;
  const minutes = Math.floor(s / 60);
  const rest = s % 60;
  return rest === 0 ? `${minutes} min` : `${minutes} min ${rest} s`;
}

/** The tail of a "come back later" sentence. `null` is the server having sent
 *  no readable Retry-After — rare, and answered with a vaguer sentence rather
 *  than an invented number. */
function inWait(seconds: number | null): string {
  return seconds === null ? "shortly" : `in ${fmtWait(seconds)}`;
}

/** Sign-in refused for time rather than for the password (POST /api/login 429). */
export function throttledSignIn(seconds: number | null): string {
  return `Too many sign-in attempts — try again ${inWait(seconds)}.`;
}

/** The operations-password gate refusing to check the password for a while
 *  (429, code ops_password_throttled). Deliberately does not say the password
 *  is wrong: the gate did not look at it. */
export function throttledOpsPassword(seconds: number | null): string {
  return (
    `Too many attempts — the operations password is temporarily refused. ` +
    `Try again ${inWait(seconds)}.`
  );
}

/** Both paths' 503. It names what did NOT happen, because the operator is
 *  looking at a password box and needs to know their password is not the
 *  problem. Retry-After is 1 s here, so there is no countdown worth running. */
export const SERVER_BUSY =
  "The server is busy — your password was not checked. Try again in a moment.";

/** Replaces a countdown when it runs out. */
export const RETRY_NOW = "You can try again now.";

/**
 * Count `seconds` down, calling `onTick` with the whole seconds remaining
 * (immediately, then about once a second) and `onDone` when it runs out.
 * Returns a stop function, which the caller MUST call when the element being
 * updated goes away.
 *
 * Remaining time is measured against a deadline rather than decremented, so a
 * backgrounded tab — where browsers throttle timers to as little as once a
 * minute — catches up on its next tick instead of holding the button down for
 * minutes after the wait has passed. A countdown that strands the operator is
 * worse than no countdown at all.
 */
export function startCountdown(
  seconds: number,
  onTick: (remaining: number) => void,
  onDone: () => void,
): () => void {
  const deadline = Date.now() + Math.max(1, Math.ceil(seconds)) * 1000;
  let timer: ReturnType<typeof setInterval> | null = null;
  const stop = (): void => {
    if (timer !== null) clearInterval(timer);
    timer = null;
  };
  onTick(Math.max(1, Math.ceil(seconds)));
  timer = setInterval(() => {
    const left = Math.ceil((deadline - Date.now()) / 1000);
    if (left > 0) {
      onTick(left);
      return;
    }
    stop();
    onDone();
  }, 1000);
  return stop;
}
